import { type FC, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Input } from "../../form/inputs/Input";
import { ClickableIcon, type IconType, Text } from "../../primitives";
import { trackingAttrs } from "../../utils";
import type { FindStatus } from "./useDocumentViewer";
import { useViewerIds } from "./viewerIds";

const SEARCH_DEBOUNCE_MS = 250;

type FindBarProps = {
	status: FindStatus;
	onSearch: (query: string, options?: { again?: boolean; previous?: boolean }) => void;
	onClose: () => void;
	/** Bumped when find is asked for again while open, to move focus back to the field. */
	focusKey: number;
};

const countLabel = (query: string, status: FindStatus) => {
	if (query.trim() === "") return "";
	if (status.pending && status.total === 0) return "Searching…";
	if (status.total === 0) return "No matches";
	return `${status.current} of ${status.total}`;
};

/** Searches as you type, highlights every match, and steps through them with Enter and Shift+Enter. */
export const FindBar: FC<FindBarProps> = ({ status, onSearch, onClose, focusKey }) => {
	const ids = useViewerIds();
	const [query, setQuery] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	const search = useDebouncedCallback((next: string) => onSearch(next), SEARCH_DEBOUNCE_MS);

	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, [focusKey]);

	const step = (previous: boolean) => onSearch(query, { again: true, previous });

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			// A search still waiting on the debounce lands on the first match; stepping first
			// would be undone when it fires.
			if (search.isPending()) search.flush();
			else step(event.shiftKey);
		} else if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			onClose();
		}
	};

	const noMatches = status.total === 0;
	const findIcon = (
		type: IconType,
		label: string,
		suffix: string,
		onClick: () => void,
		disabled = false,
	) => (
		<ClickableIcon
			type={type}
			size="small"
			color="secondary"
			aria-label={label}
			tooltip={label}
			disabled={disabled}
			onClick={onClick}
			{...ids.control(`find-${suffix}`)}
		/>
	);
	return (
		<div
			role="search"
			className="flex shrink-0 items-center gap-2 border-b border-bc-default bg-surface-base px-3 py-1.5"
			data-testid={ids.testId("find")}
		>
			<Input
				ref={inputRef}
				compact
				containerClassName="max-w-[260px]"
				type="search"
				placeholder="Find in document"
				aria-label="Find in document"
				value={query}
				onChange={(event) => {
					setQuery(event.target.value);
					search(event.target.value);
				}}
				onKeyDown={handleKeyDown}
				testId={ids.testId("find-input")}
				{...trackingAttrs(ids.trackingId("find-input"))}
				{...ids.fileTracking}
			/>
			<Text
				type="caption"
				color="secondary"
				className="min-w-[72px] whitespace-nowrap tabular-nums"
				aria-live="polite"
				data-testid={ids.testId("find-count")}
			>
				{countLabel(query, status)}
			</Text>
			{findIcon("chevron-up", "Previous match", "previous", () => step(true), noMatches)}
			{findIcon("chevron-down", "Next match", "next", () => step(false), noMatches)}
			{findIcon("x-mark", "Close find", "close", onClose)}
		</div>
	);
};
